import * as React from 'react';

import {
  Button,
  cn,
  IconButton,
  Popover,
  PopoverTrigger,
  TextArea,
  Tooltip,
} from '@rematter/ui-web';
import { LucideEdit } from 'lucide-react';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface AddNotesButtonProps {
  isDisabled?: boolean;
  initialValue?: string;
  className?: string;
  variant?: 'outline' | 'solid';
  color?: 'failure' | 'primary' | 'neutral';
  onAddNotes: (content?: string) => void;
}

export const AddNotesButton: React.FC<AddNotesButtonProps> = ({
  isDisabled = false,
  initialValue,
  className = '',
  variant = 'outline',
  color,
  onAddNotes,
}) => {
  const t = useTranslations();
  const [isOpen, setIsOpen] = React.useState(false);
  const [notesContent, setNotesContent] = React.useState(initialValue);

  // Sync internal state when initialValue changes
  React.useEffect(() => {
    setNotesContent(initialValue);
  }, [initialValue]);

  const handleSaveNotes = React.useCallback(() => {
    onAddNotes(notesContent);
    setIsOpen(false);
  }, [notesContent, onAddNotes]);

  const handleDiscardNotes = () => {
    setNotesContent(initialValue);
    setIsOpen(false);
  };

  return (
    <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Tooltip
        tip={t.materialsReconciliation.materialActions.notes.tooltip}
        wrapWithButton={false}
      >
        <IconButton
          icon={<LucideEdit size={18} />}
          variant={variant || 'outline'}
          className={cn('size-[40px]', className)}
          color={isOpen ? 'primary' : color}
          aria-label="Add notes to received line item"
          onPress={() => setIsOpen(!isOpen)}
          isDisabled={isDisabled}
        />
      </Tooltip>

      <Popover className="border-none">
        <div className="no-scrollbar flex max-h-[30vh] w-[330px] flex-col items-center gap-3 overflow-auto rounded-md border-[1px] border-neutral/10 bg-neutral p-4 shadow-lg">
          <div className="flex w-full justify-start text-sm text-white">
            {t.materialsReconciliation.materialActions.notes.title}
          </div>
          <TextArea
            value={notesContent}
            className="h-full w-full border-neutral/60 text-white [&_textarea]:bg-neutral/95"
            placeholder={
              t.materialsReconciliation.materialActions.notes.placeholder
            }
            onChange={setNotesContent}
          />
          <div className="flex w-full justify-end gap-2">
            <Button variant="outline" onPress={handleDiscardNotes}>
              {t.materialsReconciliation.materialActions.discard}
            </Button>
            <Button
              variant="solid"
              className="w-[75px]"
              onPress={handleSaveNotes}
            >
              {t.materialsReconciliation.materialActions.save}
            </Button>
          </div>
        </div>
      </Popover>
    </PopoverTrigger>
  );
};
